import { useEffect, useMemo, useRef, useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Loader2, RotateCcw } from "lucide-react";
import * as pdfjs from "pdfjs-dist";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
).toString();

function isPdf(entry) {
    return entry.type === "application/pdf" || entry.name.toLowerCase().endsWith(".pdf");
}

function PageThumb({ item, index, disabled }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.key,
        disabled,
    });
    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined, position: "relative" }}
            data-testid={`merge-page-${item.key}`}
            className={`relative border-2 bg-white select-none ${isDragging ? "border-[#0284C7] shadow-lg" : "border-black"}`}
        >
            <button
                type="button"
                {...attributes}
                {...listeners}
                disabled={disabled}
                aria-label={`Arrastar página ${item.page} de ${item.fileName} (posição ${index + 1})`}
                data-testid={`merge-drag-${item.key}`}
                className="block w-full cursor-grab active:cursor-grabbing touch-none"
            >
                {item.dataUrl ? (
                    <img src={item.dataUrl} alt={`Página ${item.page} de ${item.fileName}`} className="w-full block" draggable={false} />
                ) : (
                    <span className="block w-full aspect-[3/4] bg-[#F0F9FF] flex items-center justify-center">
                        <Loader2 className="w-4 h-4 animate-spin text-[#0284C7]" aria-hidden="true" />
                    </span>
                )}
                <span className="absolute top-0 left-0 px-1.5 py-0.5 bg-black text-[#22D3EE] font-mono-tag text-[10px] font-bold flex items-center gap-1">
                    <GripVertical className="w-3 h-3" aria-hidden="true" /> {index + 1}º
                </span>
            </button>
            <span
                className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/85 text-white font-mono-tag text-[9px] truncate"
                title={`${item.fileName} · página ${item.page}`}
            >
                {item.fileName} · pág {item.page}
            </span>
        </div>
    );
}

/**
 * Pre-visualizacao da juncao: miniaturas reais de TODAS as paginas de todos os
 * PDFs, com arrastar-soltar entre arquivos. A sequencia exibida e a sequencia
 * enviada ao backend (options.page_order).
 *
 * - ordem inicial: a ordem dos arquivos na lista (respeita A–Z/Z–A/manual);
 * - mudar arquivos ou a ordenacao da lista redefine a ordem das paginas;
 * - "Restaurar" volta para a ordem derivada da lista de arquivos.
 */
export function MergePageGrid({ fl, onChange, disabled = false }) {
    const [thumbs, setThumbs] = useState({}); // fileId -> {page -> dataUrl}
    const [counts, setCounts] = useState({}); // fileId -> numPages
    const [order, setOrder] = useState([]); // ["fileId:page"]
    const [customized, setCustomized] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const taskRef = useRef(0);

    const pdfEntries = useMemo(() => fl.displayed.filter(isPdf), [fl.displayed]);
    const signature = pdfEntries.map((e) => e.id).join("|");

    // fase 1+2: conta paginas (rapido) e renderiza miniaturas (progressivo)
    useEffect(() => {
        let cancelled = false;
        const task = ++taskRef.current;
        setThumbs({});
        setCounts({});
        setError(null);
        setProgress({ done: 0, total: 0 });

        (async () => {
            const docs = [];
            try {
                const cnt = {};
                let total = 0;
                for (const entry of pdfEntries) {
                    const buf = await entry.file.arrayBuffer();
                    const lt = pdfjs.getDocument({ data: buf });
                    const doc = await lt.promise;
                    if (cancelled || task !== taskRef.current) return;
                    docs.push({ entry, doc, lt });
                    cnt[entry.id] = doc.numPages;
                    total += doc.numPages;
                }
                if (cancelled || task !== taskRef.current) return;
                setCounts(cnt);
                setProgress({ done: 0, total });

                let done = 0;
                for (const { entry, doc } of docs) {
                    const map = {};
                    for (let i = 1; i <= doc.numPages; i++) {
                        if (cancelled || task !== taskRef.current) return;
                        const page = await doc.getPage(i);
                        const base = page.getViewport({ scale: 1 });
                        const viewport = page.getViewport({ scale: 150 / base.width });
                        const canvas = document.createElement("canvas");
                        canvas.width = viewport.width;
                        canvas.height = viewport.height;
                        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
                        map[i] = canvas.toDataURL("image/jpeg", 0.7);
                        done++;
                        setThumbs((prev) => ({ ...prev, [entry.id]: { ...(prev[entry.id] || {}), [i]: map[i] } }));
                        setProgress({ done, total });
                    }
                }
                for (const { lt } of docs) lt.destroy().catch(() => {});
            } catch (err) {
                console.error("MergePageGrid render error:", err);
                if (!cancelled && task === taskRef.current) {
                    setError("Não foi possível pré-visualizar um dos PDFs. Os arquivos serão juntados na ordem da lista acima.");
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    // ordem derivada: achata as paginas seguindo a ordem exibida dos arquivos
    const derived = useMemo(() => {
        const out = [];
        for (const e of pdfEntries) {
            const n = counts[e.id] || 0;
            for (let i = 1; i <= n; i++) out.push(`${e.id}:${i}`);
        }
        return out;
    }, [pdfEntries, counts]);

    useEffect(() => {
        setOrder(derived);
        setCustomized(false);
    }, [derived]);

    // informa o pai a sequencia vigente (somente quando a grade esta integra)
    useEffect(() => {
        if (!error && order.length > 0) onChange(order);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order, error]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const onDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setOrder((prev) => {
            const from = prev.indexOf(active.id);
            const to = prev.indexOf(over.id);
            if (from < 0 || to < 0) return prev;
            const next = [...prev];
            next.splice(to, 0, next.splice(from, 1)[0]);
            return next;
        });
        setCustomized(true);
    };

    if (error) {
        return (
            <p className="mx-4 mb-4 text-sm text-amber-800 bg-[#FFFBEB] border-2 border-black p-3" role="alert" data-testid="merge-grid-error">
                {error}
            </p>
        );
    }

    const byKey = {};
    for (const e of pdfEntries) {
        const n = counts[e.id] || 0;
        for (let i = 1; i <= n; i++) {
            byKey[`${e.id}:${i}`] = {
                key: `${e.id}:${i}`,
                fileId: e.id,
                fileName: e.name,
                page: i,
                dataUrl: thumbs[e.id]?.[i] || null,
            };
        }
    }
    const items = order.map((k) => byKey[k]).filter(Boolean);
    const loading = progress.total > 0 && progress.done < progress.total;

    if (!items.length) {
        return (
            <p className="mx-4 mb-4 font-mono-tag text-xs text-gray-600 flex items-center gap-2 py-6 justify-center" data-testid="merge-grid-loading">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Lendo os PDFs…
            </p>
        );
    }

    return (
        <div className="mx-4 mb-4 border-2 border-black p-4 bg-[#FAFAF9]" data-testid="merge-page-grid">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <span className="font-mono-tag text-xs font-bold uppercase tracking-wider text-black">
                    Ordem das páginas no PDF final
                </span>
                {customized && !disabled && (
                    <button
                        type="button"
                        onClick={() => { setOrder(derived); setCustomized(false); }}
                        data-testid="merge-restore-order"
                        title="Voltar à ordem da lista de arquivos"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 font-mono-tag text-[11px] font-bold uppercase text-gray-500 underline decoration-[#0284C7] underline-offset-2 hover:text-[#0284C7]"
                    >
                        <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Restaurar ordem
                    </button>
                )}
            </div>
            <p className="font-mono-tag text-[11px] text-gray-600 mb-2">
                Arraste as miniaturas para definir a sequência — inclusive entre arquivos diferentes.
                {loading && ` Renderizando ${progress.done}/${progress.total}…`}
            </p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={items.map((i) => i.key)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[420px] overflow-y-auto p-1">
                        {items.map((item, idx) => (
                            <PageThumb key={item.key} item={item} index={idx} disabled={disabled} />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
}
